import React, { useState, useEffect, useMemo } from 'react';
import { TrendingUp, TrendingDown, Minus, BarChart3, Calendar, ArrowLeftRight, ArrowRight, FileText, Sparkles, Loader2, CheckCircle, ChevronDown, ChevronUp, FileSpreadsheet, Percent, BarChart2, AlertCircle, Bell, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import api from '../lib/api';
import { LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';
import { useSearchParams, useNavigate } from 'react-router-dom';
import LeiTrendsFull from '../components/lei/LeiTrendsFull';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';
import { Compass, LayoutGrid } from 'lucide-react';
import { buildStageHealth } from '../lib/journeySignals';
import { JourneyHealthRibbon } from '../components/navigation-signals/JourneyHealthRibbon';
import { SignalPropagationChain } from '../components/navigation-signals/SignalPropagationChain';
import { NavigationSummaryCard } from '../components/navigation-signals/NavigationSummaryCard';
import { SignalToOutcomeTab } from '../components/navigation-signals/SignalToOutcomeTab';

const PILLAR_COLORS = {
  'P1 Access & Engagement': '#0d9488',
  'P2 Quality & Effectiveness': '#6366f1',
  'P3 Skills & Capability': '#f59e0b',
  'P4 Efficiency & ROI': '#ec4899'
};

const MEASURE_COLORS = [
  '#0d9488', '#6366f1', '#f59e0b', '#ec4899', '#3b82f6',
  '#10b981', '#8b5cf6', '#f97316', '#ef4444', '#06b6d4',
  '#84cc16', '#a855f7', '#14b8a6', '#f43f5e', '#22d3ee'
];

const PERIOD_PRESETS = [
  { key: 'q1', label: 'Q1', getRange: (year) => ({ start: `${year}-01-01`, end: `${year}-03-31` }) },
  { key: 'q2', label: 'Q2', getRange: (year) => ({ start: `${year}-04-01`, end: `${year}-06-30` }) },
  { key: 'q3', label: 'Q3', getRange: (year) => ({ start: `${year}-07-01`, end: `${year}-09-30` }) },
  { key: 'q4', label: 'Q4', getRange: (year) => ({ start: `${year}-10-01`, end: `${year}-12-31` }) },
  { key: 'h1', label: 'H1', getRange: (year) => ({ start: `${year}-01-01`, end: `${year}-06-30` }) },
  { key: 'h2', label: 'H2', getRange: (year) => ({ start: `${year}-07-01`, end: `${year}-12-31` }) },
  { key: 'last3m', label: 'Last 3M', getRange: (year, refDate) => {
    const end = new Date(refDate); const start = new Date(refDate);
    start.setMonth(start.getMonth() - 3);
    return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
  }},
  { key: 'prev3m', label: 'Prev 3M', getRange: (year, refDate) => {
    const end = new Date(refDate); end.setMonth(end.getMonth() - 3);
    const start = new Date(end); start.setMonth(start.getMonth() - 3);
    return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
  }}
];

export default function ScoreTrendsPage() {
  const { isSuperAdmin, viewAsOrg } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'lei');
  // Navigation Signals: dedicated Navigation view (default) vs legacy All Metrics tabs.
  const [viewMode, setViewMode] = useState(
    searchParams.get('tab') && ['lei', 'kpi', 'overlay'].includes(searchParams.get('tab')) ? 'metrics' : 'navigation'
  );

  // Organization filter — use URL param > viewAsOrg > 'all'
  const [organizations, setOrganizations] = useState([]);
  const initialOrg = searchParams.get('organization_id') || viewAsOrg?.id || 'all';
  const [selectedOrg, setSelectedOrg] = useState(initialOrg);

  // LEI state
  const [trendsData, setTrendsData] = useState(null);
  const [selectedPillar, setSelectedPillar] = useState('all');
  const [selectedMeasure, setSelectedMeasure] = useState('all');
  const [leiDateRange, setLeiDateRange] = useState([0, 100]);
  const [isLeiCompareExpanded, setIsLeiCompareExpanded] = useState(false);
  const [leiPeriodA, setLeiPeriodA] = useState('q1');
  const [leiPeriodB, setLeiPeriodB] = useState('q2');

  // KPI state
  const [kpiTrendsData, setKpiTrendsData] = useState(null);
  const [selectedKpi, setSelectedKpi] = useState('all');
  const [kpiDateRange, setKpiDateRange] = useState([0, 100]);
  const [isKpiCompareExpanded, setIsKpiCompareExpanded] = useState(false);
  const [kpiPeriodA, setKpiPeriodA] = useState('q1');
  const [kpiPeriodB, setKpiPeriodB] = useState('q2');

  // Combined overlay state
  const [selectedLeiOverlays, setSelectedLeiOverlays] = useState([]);
  const [selectedKpiOverlays, setSelectedKpiOverlays] = useState([]);

  // View mode: 'absolute' (dual Y-axis by scale group) or 'normalized' (% change from baseline)
  const [kpiViewMode, setKpiViewMode] = useState('absolute');
  const [overlayViewMode, setOverlayViewMode] = useState('absolute');
  const [sigmaThreshold, setSigmaThreshold] = useState(2);

  // Export & AI state
  const [isExportingLei, setIsExportingLei] = useState(false);
  const [isExportingKpi, setIsExportingKpi] = useState(false);
  const [leiAiInsights, setLeiAiInsights] = useState(null);
  const [kpiAiInsights, setKpiAiInsights] = useState(null);
  const [isGeneratingLeiInsights, setIsGeneratingLeiInsights] = useState(false);
  const [isGeneratingKpiInsights, setIsGeneratingKpiInsights] = useState(false);

  // Anomaly alert subscription state
  const [anomalySub, setAnomalySub] = useState(null);
  const [isSubSaving, setIsSubSaving] = useState(false);
  const [isSubExpanded, setIsSubExpanded] = useState(false);

  useEffect(() => {
    const params = selectedOrg && selectedOrg !== 'all' ? `?organization_id=${selectedOrg}` : '';
    api.get(`/import/data/lei-baselines/trends${params}`).then(r => setTrendsData(r.data)).catch(() => {});
    api.get(`/import/data/kpi-actuals/trends${params}`).then(r => setKpiTrendsData(r.data)).catch(() => {});
  }, [selectedOrg]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    api.get('/rbac/organizations').then(r => setOrganizations(r.data.organizations || [])).catch(() => {});
  }, [isSuperAdmin]);

  // Fetch anomaly subscription
  useEffect(() => {
    api.get('/data-management/anomaly-subscriptions').then(r => setAnomalySub(r.data)).catch(() => {});
  }, []);

  const saveAnomalySub = async (updates) => {
    setIsSubSaving(true);
    try {
      const payload = { ...anomalySub, ...updates };
      const res = await api.post('/data-management/anomaly-subscriptions', payload);
      setAnomalySub(res.data.subscription);
      toast.success('Alert preferences saved');
    } catch { toast.error('Failed to save alert preferences'); }
    finally { setIsSubSaving(false); }
  };

  // ── Utility helpers ────────────────────────────────────────────────────
  const isValidDate = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    return !isNaN(date.getTime()) && date.getFullYear() >= 2000;
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch { return ''; }
  };

  const getDateFromSliderValue = (dates, percentage) => {
    if (dates.length === 0) return null;
    const index = Math.round((percentage / 100) * (dates.length - 1));
    return dates[Math.min(Math.max(index, 0), dates.length - 1)];
  };

  const getPresetDateRange = (dates, preset) => {
    if (dates.length === 0) return [0, 100];
    const endDate = new Date(dates[dates.length - 1]);
    let startDate;
    switch (preset) {
      case '3m': startDate = new Date(endDate); startDate.setMonth(startDate.getMonth() - 3); break;
      case '6m': startDate = new Date(endDate); startDate.setMonth(startDate.getMonth() - 6); break;
      case 'ytd': startDate = new Date(endDate.getFullYear(), 0, 1); break;
      case '1y': startDate = new Date(endDate); startDate.setFullYear(startDate.getFullYear() - 1); break;
      default: return [0, 100];
    }
    let startIndex = 0;
    for (let i = 0; i < dates.length; i++) { if (new Date(dates[i]) >= startDate) { startIndex = i; break; } }
    return [Math.round((startIndex / (dates.length - 1)) * 100), 100];
  };

  const isPresetActive = (dates, currentRange, preset) => {
    const presetRange = getPresetDateRange(dates, preset);
    return currentRange[0] === presetRange[0] && currentRange[1] === presetRange[1];
  };

  // ── LEI computed values ────────────────────────────────────────────────
  const allLeiDates = useMemo(() => {
    if (!trendsData?.trends) return [];
    const dateSet = new Set();
    Object.values(trendsData.trends).forEach(m => m.data_points.forEach(dp => {
      const d = dp.measurement_date?.split('T')[0] || dp.measurement_date;
      if (d && isValidDate(d)) dateSet.add(d);
    }));
    return Array.from(dateSet).sort((a, b) => new Date(a) - new Date(b));
  }, [trendsData]);

  const leiStartDate = getDateFromSliderValue(allLeiDates, leiDateRange[0]);
  const leiEndDate = getDateFromSliderValue(allLeiDates, leiDateRange[1]);
  const hasTrendData = trendsData?.total_data_points > 0;

  const getPillars = () => {
    if (!trendsData?.trends) return [];
    const pillars = new Set();
    Object.values(trendsData.trends).forEach(m => { if (m.pillar) pillars.add(m.pillar); });
    return Array.from(pillars).sort();
  };

  const getMeasures = () => {
    if (!trendsData?.trends) return [];
    return Object.entries(trendsData.trends)
      .filter(([, m]) => selectedPillar === 'all' || m.pillar === selectedPillar)
      .map(([id, m]) => ({ id, name: m.measure_name })).sort((a, b) => a.name.localeCompare(b.name));
  };

  const chartData = useMemo(() => {
    if (!trendsData?.trends) return [];
    const dateMap = {};
    Object.entries(trendsData.trends).forEach(([measureId, measureData]) => {
      if (selectedPillar !== 'all' && measureData.pillar !== selectedPillar) return;
      if (selectedMeasure !== 'all' && measureId !== selectedMeasure) return;
      measureData.data_points.forEach(dp => {
        const date = dp.measurement_date?.split('T')[0] || dp.measurement_date;
        if (!date || !isValidDate(date)) return;
        if (leiStartDate && leiEndDate) {
          const dpDate = new Date(date);
          if (dpDate < new Date(leiStartDate) || dpDate > new Date(leiEndDate)) return;
        }
        if (!dateMap[date]) dateMap[date] = { date };
        dateMap[date][measureId] = dp.calculated_score;
      });
    });
    return Object.values(dateMap).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [trendsData, selectedPillar, selectedMeasure, leiStartDate, leiEndDate]);

  const visibleMeasures = useMemo(() => {
    if (!trendsData?.trends) return [];
    return Object.entries(trendsData.trends)
      .filter(([measureId, measureData]) => {
        if (selectedPillar !== 'all' && measureData.pillar !== selectedPillar) return false;
        if (selectedMeasure !== 'all' && measureId !== selectedMeasure) return false;
        return true;
      })
      .map(([measureId, measureData], index) => ({
        measureId, name: measureData.measure_name, pillar: measureData.pillar,
        color: MEASURE_COLORS[index % MEASURE_COLORS.length], trend: measureData.trend
      }));
  }, [trendsData, selectedPillar, selectedMeasure]);

  // ── Navigation Signals: journey-stage health from existing LEI trends (no new math) ──
  const stageHealth = useMemo(() => {
    if (!trendsData?.trends) return null;
    return buildStageHealth(trendsData.trends);
  }, [trendsData]);

  // ── KPI computed values ────────────────────────────────────────────────
  const allKpiDates = useMemo(() => {
    if (!kpiTrendsData?.by_kpi) return [];
    const dateSet = new Set();
    Object.values(kpiTrendsData.by_kpi).forEach(k => k.data_points.forEach(dp => {
      const d = dp.date?.split('T')[0] || dp.date;
      if (d && isValidDate(d)) dateSet.add(d);
    }));
    return Array.from(dateSet).sort((a, b) => new Date(a) - new Date(b));
  }, [kpiTrendsData]);

  const kpiStartDate = getDateFromSliderValue(allKpiDates, kpiDateRange[0]);
  const kpiEndDate = getDateFromSliderValue(allKpiDates, kpiDateRange[1]);
  const hasKpiTrendData = kpiTrendsData?.total_kpis_tracked > 0;

  const getKpiList = () => {
    if (!kpiTrendsData?.by_kpi) return [];
    return Object.entries(kpiTrendsData.by_kpi).map(([id, data]) => ({ id, name: data.kpi_name })).sort((a, b) => a.name.localeCompare(b.name));
  };

  const kpiChartData = useMemo(() => {
    if (!kpiTrendsData?.by_kpi) return [];
    const dateMap = {};
    Object.entries(kpiTrendsData.by_kpi).forEach(([kpiId, kpiData]) => {
      if (selectedKpi !== 'all' && kpiId !== selectedKpi) return;
      kpiData.data_points.forEach(dp => {
        const date = dp.date?.split('T')[0] || dp.date;
        if (!date || !isValidDate(date)) return;
        if (kpiStartDate && kpiEndDate) {
          const dpDate = new Date(date);
          if (dpDate < new Date(kpiStartDate) || dpDate > new Date(kpiEndDate)) return;
        }
        if (!dateMap[date]) dateMap[date] = { date };
        dateMap[date][kpiId] = dp.value;
      });
    });
    return Object.values(dateMap).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [kpiTrendsData, selectedKpi, kpiStartDate, kpiEndDate]);

  const visibleKpis = useMemo(() => {
    if (!kpiTrendsData?.by_kpi) return [];
    return Object.entries(kpiTrendsData.by_kpi)
      .filter(([kpiId]) => selectedKpi === 'all' || kpiId === selectedKpi)
      .map(([kpiId, kpiData], index) => ({
        kpiId, name: kpiData.kpi_name, unit: kpiData.unit,
        color: MEASURE_COLORS[index % MEASURE_COLORS.length], inverse: kpiData.inverse
      }));
  }, [kpiTrendsData, selectedKpi]);

  // ── KPI scale grouping: separate $ from score/% ──────────────────────
  const MONETARY_UNITS = new Set(['$', 'USD', 'EUR', 'GBP', 'CAD', 'AUD']);

  const kpiScaleGroups = useMemo(() => {
    const monetary = visibleKpis.filter(k => MONETARY_UNITS.has(k.unit));
    const standard = visibleKpis.filter(k => !MONETARY_UNITS.has(k.unit));
    return { monetary, standard, hasDualScale: monetary.length > 0 && standard.length > 0 };
  }, [visibleKpis]);

  // Normalized chart data: % change from first data point for each KPI
  const kpiNormalizedData = useMemo(() => {
    if (kpiViewMode !== 'normalized' || !kpiChartData.length) return [];
    // Find baseline (first non-null value) for each KPI
    const baselines = {};
    for (const kpi of visibleKpis) {
      for (const row of kpiChartData) {
        if (row[kpi.kpiId] != null) { baselines[kpi.kpiId] = row[kpi.kpiId]; break; }
      }
    }
    return kpiChartData.map(row => {
      const out = { date: row.date };
      for (const kpi of visibleKpis) {
        const val = row[kpi.kpiId];
        const base = baselines[kpi.kpiId];
        if (val != null && base != null && base !== 0) {
          out[kpi.kpiId] = ((val - base) / base) * 100;
        }
      }
      return out;
    });
  }, [kpiChartData, visibleKpis, kpiViewMode]);

  // ── Anomaly detection: compute per-KPI mean/stddev and flag Nσ outliers ──
  const kpiAnomalies = useMemo(() => {
    if (kpiViewMode !== 'normalized' || !kpiNormalizedData.length || visibleKpis.length === 0) {
      return { stats: {}, outlierData: [], upperBound: null, lowerBound: null };
    }
    // Collect all % change values per KPI
    const allValues = [];
    const perKpi = {};
    for (const kpi of visibleKpis) {
      perKpi[kpi.kpiId] = [];
    }
    for (const row of kpiNormalizedData) {
      for (const kpi of visibleKpis) {
        const v = row[kpi.kpiId];
        if (v != null) { perKpi[kpi.kpiId].push(v); allValues.push(v); }
      }
    }
    if (allValues.length < 3) return { stats: {}, outlierData: [], upperBound: null, lowerBound: null };
    // Pooled mean/stddev across all KPIs
    const mean = allValues.reduce((a, b) => a + b, 0) / allValues.length;
    const variance = allValues.reduce((a, v) => a + (v - mean) ** 2, 0) / allValues.length;
    const stddev = Math.sqrt(variance);
    const upper = mean + sigmaThreshold * stddev;
    const lower = mean - sigmaThreshold * stddev;
    // Flag outlier points
    const outlierData = kpiNormalizedData.map(row => {
      const out = { date: row.date };
      let hasOutlier = false;
      for (const kpi of visibleKpis) {
        const v = row[kpi.kpiId];
        if (v != null && (v > upper || v < lower)) {
          out[`${kpi.kpiId}_outlier`] = v;
          hasOutlier = true;
        }
      }
      return hasOutlier ? out : null;
    }).filter(Boolean);
    return {
      stats: { mean, stddev, upper, lower },
      outlierData,
      upperBound: upper,
      lowerBound: lower,
    };
  }, [kpiNormalizedData, visibleKpis, kpiViewMode, sigmaThreshold]);

  // ── Combined overlay computed values ─────────────────────────────────
  const availableLeiMeasures = useMemo(() => {
    if (!trendsData?.trends) return [];
    return Object.entries(trendsData.trends)
      .map(([id, m]) => ({ id, name: m.measure_name, pillar: m.pillar }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [trendsData]);

  const availableKpis = useMemo(() => {
    if (!kpiTrendsData?.by_kpi) return [];
    return Object.entries(kpiTrendsData.by_kpi)
      .map(([id, d]) => ({ id, name: d.kpi_name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [kpiTrendsData]);

  const overlayChartData = useMemo(() => {
    if (selectedLeiOverlays.length === 0 && selectedKpiOverlays.length === 0) return [];
    const dateMap = {};
    // Add LEI data points
    if (trendsData?.trends) {
      selectedLeiOverlays.forEach(mid => {
        const m = trendsData.trends[mid];
        if (!m) return;
        m.data_points.forEach(dp => {
          const date = dp.measurement_date?.split('T')[0] || dp.measurement_date;
          if (!date || !isValidDate(date)) return;
          if (!dateMap[date]) dateMap[date] = { date };
          dateMap[date][`lei_${mid}`] = dp.calculated_score;
        });
      });
    }
    // Add KPI data points
    if (kpiTrendsData?.by_kpi) {
      selectedKpiOverlays.forEach(kid => {
        const k = kpiTrendsData.by_kpi[kid];
        if (!k) return;
        k.data_points.forEach(dp => {
          const date = dp.date?.split('T')[0] || dp.date;
          if (!date || !isValidDate(date)) return;
          if (!dateMap[date]) dateMap[date] = { date };
          dateMap[date][`kpi_${kid}`] = dp.value;
        });
      });
    }
    return Object.values(dateMap).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [trendsData, kpiTrendsData, selectedLeiOverlays, selectedKpiOverlays, isValidDate]);

  // Overlay normalized data (% change from first data point)
  const overlayNormalizedData = useMemo(() => {
    if (overlayViewMode !== 'normalized' || !overlayChartData.length) return [];
    const baselines = {};
    const allKeys = [
      ...selectedLeiOverlays.map(id => `lei_${id}`),
      ...selectedKpiOverlays.map(id => `kpi_${id}`)
    ];
    for (const key of allKeys) {
      for (const row of overlayChartData) {
        if (row[key] != null) { baselines[key] = row[key]; break; }
      }
    }
    return overlayChartData.map(row => {
      const out = { date: row.date };
      for (const key of allKeys) {
        const val = row[key];
        const base = baselines[key];
        if (val != null && base != null && base !== 0) {
          out[key] = ((val - base) / base) * 100;
        }
      }
      return out;
    });
  }, [overlayChartData, overlayViewMode, selectedLeiOverlays, selectedKpiOverlays]);

  const LEI_OVERLAY_COLORS = ['#0d9488', '#6366f1', '#f59e0b', '#3b82f6', '#10b981', '#8b5cf6'];
  const KPI_OVERLAY_COLORS = ['#ef4444', '#ec4899', '#f97316', '#06b6d4', '#84cc16', '#a855f7'];

  const toggleOverlay = (id, list, setList) => {
    setList(prev => prev.includes(id) ? prev.filter(x => x !== id) : prev.length < 6 ? [...prev, id] : prev);
  };

  // ── Period comparison helpers ──────────────────────────────────────────
  const calculatePeriodMetrics = (trendsData, periodKey, dates) => {
    if (!trendsData?.trends || !dates || dates.length === 0) return null;
    const latestDate = dates[dates.length - 1];
    const latestYear = new Date(latestDate).getFullYear();
    const preset = PERIOD_PRESETS.find(p => p.key === periodKey);
    if (!preset) return null;
    const { start, end } = preset.getRange(latestYear, latestDate);
    const startDate = new Date(start); const endDate = new Date(end);
    const metrics = {}; let totalAvg = 0; let measureCount = 0;
    Object.entries(trendsData.trends).forEach(([measureId, measureData]) => {
      const pts = measureData.data_points.filter(dp => {
        const d = new Date(dp.measurement_date?.split('T')[0] || dp.measurement_date);
        return d >= startDate && d <= endDate;
      });
      if (pts.length > 0) {
        const scores = pts.map(p => p.calculated_score);
        const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
        metrics[measureId] = { name: measureData.measure_name, pillar: measureData.pillar, avg, latest: scores[scores.length - 1], first: scores[0], change: scores[scores.length - 1] - scores[0], dataPoints: pts.length };
        totalAvg += avg; measureCount++;
      }
    });
    return { periodLabel: preset.label, startDate: start, endDate: end, measures: metrics, overallAvg: measureCount > 0 ? totalAvg / measureCount : 0, measureCount };
  };

  const calculateKpiPeriodMetrics = (kpiTrendsData, periodKey, dates) => {
    if (!kpiTrendsData?.by_kpi || !dates || dates.length === 0) return null;
    const latestDate = dates[dates.length - 1];
    const latestYear = new Date(latestDate).getFullYear();
    const preset = PERIOD_PRESETS.find(p => p.key === periodKey);
    if (!preset) return null;
    const { start, end } = preset.getRange(latestYear, latestDate);
    const startDate = new Date(start); const endDate = new Date(end);
    const metrics = {};
    Object.entries(kpiTrendsData.by_kpi).forEach(([kpiId, kpiData]) => {
      const pts = kpiData.data_points.filter(dp => {
        const d = new Date(dp.date?.split('T')[0] || dp.date);
        return d >= startDate && d <= endDate;
      });
      if (pts.length > 0) {
        const values = pts.map(p => p.value);
        const avg = values.reduce((a, b) => a + b, 0) / values.length;
        metrics[kpiId] = { name: kpiData.kpi_name, unit: kpiData.unit, inverse: kpiData.inverse, avg, latest: values[values.length - 1], first: values[0], change: values[values.length - 1] - values[0], changePct: values[0] !== 0 ? ((values[values.length - 1] - values[0]) / values[0]) * 100 : 0, dataPoints: pts.length };
      }
    });
    return { periodLabel: preset.label, startDate: start, endDate: end, kpis: metrics, kpiCount: Object.keys(metrics).length };
  };

  const leiPeriodAMetrics = useMemo(() => calculatePeriodMetrics(trendsData, leiPeriodA, allLeiDates), [trendsData, leiPeriodA, allLeiDates]);
  const leiPeriodBMetrics = useMemo(() => calculatePeriodMetrics(trendsData, leiPeriodB, allLeiDates), [trendsData, leiPeriodB, allLeiDates]);
  const kpiPeriodAMetrics = useMemo(() => calculateKpiPeriodMetrics(kpiTrendsData, kpiPeriodA, allKpiDates), [kpiTrendsData, kpiPeriodA, allKpiDates]);
  const kpiPeriodBMetrics = useMemo(() => calculateKpiPeriodMetrics(kpiTrendsData, kpiPeriodB, allKpiDates), [kpiTrendsData, kpiPeriodB, allKpiDates]);

  // ── Export & AI ────────────────────────────────────────────────────────
  const exportPeriodComparison = async (dataType, periodAMetrics, periodBMetrics, format = 'pdf') => {
    const setExporting = dataType === 'lei' ? setIsExportingLei : setIsExportingKpi;
    setExporting(true);
    try {
      const metricsKey = dataType === 'lei' ? 'measures' : 'kpis';
      const payload = { data_type: dataType, period_a: { label: periodAMetrics?.periodLabel || 'Period A', startDate: periodAMetrics?.startDate || '', endDate: periodAMetrics?.endDate || '', metrics: periodAMetrics?.[metricsKey] || {} }, period_b: { label: periodBMetrics?.periodLabel || 'Period B', startDate: periodBMetrics?.startDate || '', endDate: periodBMetrics?.endDate || '', metrics: periodBMetrics?.[metricsKey] || {} }, export_format: format };
      const response = await api.post('/import/period-comparison/export', payload, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a'); link.href = url;
      const ts = new Date().toISOString().slice(0, 16).replace(/[-:]/g, '').replace('T', '_');
      link.download = `Period_Comparison_${dataType.toUpperCase()}_${ts}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link); window.URL.revokeObjectURL(url);
      toast.success(`Period comparison exported as ${format.toUpperCase()}`);
    } catch (error) { toast.error('Failed to export: ' + (error.response?.data?.detail || error.message)); }
    finally { setExporting(false); }
  };

  const generateAiInsights = async (dataType, periodAMetrics, periodBMetrics) => {
    const setGenerating = dataType === 'lei' ? setIsGeneratingLeiInsights : setIsGeneratingKpiInsights;
    const setInsights = dataType === 'lei' ? setLeiAiInsights : setKpiAiInsights;
    setGenerating(true);
    try {
      const metricsKey = dataType === 'lei' ? 'measures' : 'kpis';
      const payload = { data_type: dataType, period_a: { label: periodAMetrics?.periodLabel || 'Period A', startDate: periodAMetrics?.startDate || '', endDate: periodAMetrics?.endDate || '', metrics: periodAMetrics?.[metricsKey] || {} }, period_b: { label: periodBMetrics?.periodLabel || 'Period B', startDate: periodBMetrics?.startDate || '', endDate: periodBMetrics?.endDate || '', metrics: periodBMetrics?.[metricsKey] || {} } };
      const response = await api.post('/import/period-comparison/generate-insights', payload);
      if (response.data.error) { toast.error(response.data.error); return; }
      setInsights(response.data);
      toast.success('AI insights generated!');
    } catch (error) { toast.error('Failed to generate insights: ' + (error.response?.data?.detail || error.message)); }
    finally { setGenerating(false); }
  };

  const exportWithAiInsights = async (dataType, periodAMetrics, periodBMetrics, format, aiInsights) => {
    const setExporting = dataType === 'lei' ? setIsExportingLei : setIsExportingKpi;
    setExporting(true);
    try {
      const metricsKey = dataType === 'lei' ? 'measures' : 'kpis';
      const payload = { data_type: dataType, period_a: { label: periodAMetrics?.periodLabel || 'Period A', startDate: periodAMetrics?.startDate || '', endDate: periodAMetrics?.endDate || '', metrics: periodAMetrics?.[metricsKey] || {} }, period_b: { label: periodBMetrics?.periodLabel || 'Period B', startDate: periodBMetrics?.startDate || '', endDate: periodBMetrics?.endDate || '', metrics: periodBMetrics?.[metricsKey] || {} }, export_format: format, include_ai_insights: true, ai_insights: aiInsights?.insights || '' };
      const response = await api.post('/import/period-comparison/export', payload, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a'); link.href = url;
      const ts = new Date().toISOString().slice(0, 16).replace(/[-:]/g, '').replace('T', '_');
      link.download = `Period_Comparison_${dataType.toUpperCase()}_with_AI_${ts}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link); window.URL.revokeObjectURL(url);
      toast.success(`Exported with AI insights`);
    } catch (error) { toast.error('Failed to export: ' + (error.response?.data?.detail || error.message)); }
    finally { setExporting(false); }
  };

  // ── Custom Tooltip ─────────────────────────────────────────────────────
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
        <p className="text-sm font-medium text-slate-700 mb-2">{formatShortDate(label)}</p>
        {payload.map((entry, index) => (
          <div key={entry.name || `entry-${index}`} className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-slate-600">{entry.name}:</span>
            <span className="font-semibold" style={{ color: entry.color }}>{entry.value?.toFixed(1)}</span>
          </div>
        ))}
      </div>
    );
  };

  // Tooltip for KPI dual-axis / normalized chart
  const KpiTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const isNorm = kpiViewMode === 'normalized';
    const hasAnomalyBounds = isNorm && kpiAnomalies.stats.upper != null;
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200 max-w-xs">
        <p className="text-sm font-medium text-slate-700 mb-2">{formatShortDate(label)}</p>
        {payload.map((entry, index) => {
          const kpi = visibleKpis.find(k => k.kpiId === entry.dataKey);
          const unit = kpi?.unit || '';
          const prefix = unit === '$' && !isNorm ? '$' : '';
          const suffix = isNorm ? '%' : (unit === '%' ? '%' : '');
          const formatted = isNorm
            ? `${entry.value >= 0 ? '+' : ''}${entry.value?.toFixed(1)}%`
            : `${prefix}${entry.value?.toLocaleString(undefined, { maximumFractionDigits: 1 })}${suffix}`;
          const isOutlier = hasAnomalyBounds && entry.value != null &&
            (entry.value > kpiAnomalies.stats.upper || entry.value < kpiAnomalies.stats.lower);
          return (
            <div key={entry.name || `entry-${index}`} className="flex items-center gap-2 text-sm">
              <div className={`w-3 h-3 rounded-full flex-shrink-0 ${isOutlier ? 'ring-2 ring-rose-400' : ''}`} style={{ backgroundColor: isOutlier ? '#ef4444' : entry.color }} />
              <span className="text-slate-600 truncate">{entry.name}:</span>
              <span className="font-semibold flex-shrink-0" style={{ color: isOutlier ? '#ef4444' : entry.color }}>{formatted}</span>
              {isOutlier && <span className="text-[10px] text-rose-500 font-medium">OUTLIER</span>}
            </div>
          );
        })}
        {hasAnomalyBounds && (
          <p className="text-[10px] text-slate-400 mt-2 border-t border-slate-100 pt-1">
            Anomaly bounds: {kpiAnomalies.stats.lower?.toFixed(0)}% to {kpiAnomalies.stats.upper?.toFixed(0)}% ({sigmaThreshold}σ)
          </p>
        )}
      </div>
    );
  };

  // Tooltip for overlay normalized chart
  const OverlayTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const isNorm = overlayViewMode === 'normalized';
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200 max-w-xs">
        <p className="text-sm font-medium text-slate-700 mb-2">{formatShortDate(label)}</p>
        {payload.map((entry, index) => {
          const formatted = isNorm
            ? `${entry.value >= 0 ? '+' : ''}${entry.value?.toFixed(1)}%`
            : entry.value?.toLocaleString(undefined, { maximumFractionDigits: 1 });
          return (
            <div key={entry.name || `entry-${index}`} className="flex items-center gap-2 text-sm">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
              <span className="text-slate-600 truncate">{entry.name}:</span>
              <span className="font-semibold flex-shrink-0" style={{ color: entry.color }}>{formatted}</span>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Shared renderers ───────────────────────────────────────────────────
  const renderDateRangeSlider = (dates, dateRange, setDateRange, startDate, endDate, accentColor = 'indigo') => {
    if (dates.length <= 1) return null;
    return (
      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <label className={`text-sm font-medium text-slate-700 flex items-center gap-2`}>
            <Calendar className={`h-4 w-4 text-${accentColor}-500`} /> Date Range
          </label>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600 bg-white px-2 py-0.5 rounded border">{formatShortDate(startDate)}</span>
            <span className="text-xs text-slate-400">to</span>
            <span className="text-sm text-slate-600 bg-white px-2 py-0.5 rounded border">{formatShortDate(endDate)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs text-slate-500">Quick select:</span>
          <div className="flex gap-1.5">
            {[{ key: '3m', label: '3M' }, { key: '6m', label: '6M' }, { key: 'ytd', label: 'YTD' }, { key: '1y', label: '1Y' }, { key: 'all', label: 'All' }].map(({ key, label }) => (
              <Button key={key} variant={isPresetActive(dates, dateRange, key) ? "default" : "outline"} size="sm"
                className={`h-7 px-2.5 text-xs ${isPresetActive(dates, dateRange, key) ? `bg-${accentColor}-600 hover:bg-${accentColor}-700 text-white` : `text-slate-600 hover:text-${accentColor}-600`}`}
                onClick={() => setDateRange(getPresetDateRange(dates, key))}>{label}</Button>
            ))}
          </div>
        </div>
        <div className="px-2">
          <Slider value={dateRange} onValueChange={setDateRange} min={0} max={100} step={1} className="w-full" />
        </div>
        <div className="flex justify-between mt-2 text-xs text-slate-400">
          <span>{formatShortDate(dates[0])}</span>
          <span>{dates.length} data points</span>
          <span>{formatShortDate(dates[dates.length - 1])}</span>
        </div>
      </div>
    );
  };

  const renderCompareSection = (dataType, periodA, setPeriodA, periodB, setPeriodB, isExpanded, setIsExpanded, periodAMetrics, periodBMetrics, aiInsights, isGenerating, isExporting) => {
    const metricsKey = dataType === 'lei' ? 'measures' : 'kpis';
    const metricLabel = dataType === 'lei' ? 'measure' : 'KPI';
    return (
      <div className="mt-6 border border-purple-200 rounded-lg bg-gradient-to-br from-purple-50 to-white">
        <button onClick={() => setIsExpanded(!isExpanded)} className="w-full p-4 flex items-center justify-between hover:bg-purple-50/50 transition-colors rounded-t-lg">
          <div className="flex items-center gap-2">
            <ArrowLeftRight className="h-5 w-5 text-purple-600" />
            <span className="font-semibold text-slate-800">Compare Periods</span>
            <Badge variant="outline" className="text-purple-600 border-purple-300">Period vs Period</Badge>
          </div>
          {isExpanded ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
        </button>
        {isExpanded && (
          <div className="p-4 pt-0 space-y-4">
            {/* Period selectors */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span className="text-sm font-medium text-slate-700">Period A:</span>
                <Select value={periodA} onValueChange={setPeriodA}>
                  <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>{PERIOD_PRESETS.map(p => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400 hidden sm:block" />
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                <span className="text-sm font-medium text-slate-700">Period B:</span>
                <Select value={periodB} onValueChange={setPeriodB}>
                  <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>{PERIOD_PRESETS.map(p => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {/* Date range badges */}
            {periodAMetrics && periodBMetrics && (
              <div className="flex gap-4 text-xs">
                <div className="flex items-center gap-1.5 text-blue-600 bg-blue-50 px-2 py-1 rounded"><Calendar className="h-3 w-3" /><span>{formatShortDate(periodAMetrics.startDate)} - {formatShortDate(periodAMetrics.endDate)}</span></div>
                <div className="flex items-center gap-1.5 text-emerald-600 bg-emerald-50 px-2 py-1 rounded"><Calendar className="h-3 w-3" /><span>{formatShortDate(periodBMetrics.startDate)} - {formatShortDate(periodBMetrics.endDate)}</span></div>
              </div>
            )}
            {/* Comparison results */}
            {periodAMetrics && periodBMetrics && (
              <div className="space-y-4">
                {dataType === 'lei' && (
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-blue-600 font-medium">{periodAMetrics.periodLabel} Avg</p>
                      <p className="text-xl font-bold text-blue-700">{periodAMetrics.overallAvg.toFixed(1)}</p>
                      <p className="text-xs text-blue-500">{periodAMetrics.measureCount} measures</p>
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-emerald-600 font-medium">{periodBMetrics.periodLabel} Avg</p>
                      <p className="text-xl font-bold text-emerald-700">{periodBMetrics.overallAvg.toFixed(1)}</p>
                      <p className="text-xs text-emerald-500">{periodBMetrics.measureCount} measures</p>
                    </div>
                    <div className={`rounded-lg p-3 text-center ${periodBMetrics.overallAvg > periodAMetrics.overallAvg ? 'bg-emerald-50' : periodBMetrics.overallAvg < periodAMetrics.overallAvg ? 'bg-rose-50' : 'bg-slate-50'}`}>
                      <p className="text-xs text-slate-600 font-medium">Change</p>
                      <p className={`text-xl font-bold flex items-center justify-center gap-1 ${periodBMetrics.overallAvg > periodAMetrics.overallAvg ? 'text-emerald-600' : periodBMetrics.overallAvg < periodAMetrics.overallAvg ? 'text-rose-600' : 'text-slate-600'}`}>
                        {periodBMetrics.overallAvg > periodAMetrics.overallAvg && <TrendingUp className="h-5 w-5" />}
                        {periodBMetrics.overallAvg < periodAMetrics.overallAvg && <TrendingDown className="h-5 w-5" />}
                        {periodBMetrics.overallAvg > periodAMetrics.overallAvg ? '+' : ''}
                        {(periodBMetrics.overallAvg - periodAMetrics.overallAvg).toFixed(1)}
                      </p>
                      <p className="text-xs text-slate-500">{((periodBMetrics.overallAvg - periodAMetrics.overallAvg) / periodAMetrics.overallAvg * 100).toFixed(1)}%</p>
                    </div>
                  </div>
                )}
                {/* Per-metric comparison */}
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-slate-700">Per-{metricLabel} Comparison</h4>
                  <div className="max-h-64 overflow-y-auto space-y-2 pr-2">
                    {Object.entries(periodBMetrics[metricsKey]).map(([id, metricB]) => {
                      const metricA = periodAMetrics[metricsKey][id];
                      if (!metricA) return null;
                      const change = metricB.avg - metricA.avg;
                      const changePct = metricA.avg !== 0 ? (change / metricA.avg) * 100 : 0;
                      return (
                        <div key={id} className="flex items-center gap-3 p-2 bg-white rounded border border-slate-100">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate">{metricB.name}</p>
                            {metricB.pillar && <p className="text-xs text-slate-400">{metricB.pillar}</p>}
                          </div>
                          <div className="text-center px-2"><p className="text-xs text-blue-500">{periodAMetrics.periodLabel}</p><p className="font-semibold text-blue-700">{metricA.avg.toFixed(1)}</p></div>
                          <ArrowRight className="h-4 w-4 text-slate-300 flex-shrink-0" />
                          <div className="text-center px-2"><p className="text-xs text-emerald-500">{periodBMetrics.periodLabel}</p><p className="font-semibold text-emerald-700">{metricB.avg.toFixed(1)}</p></div>
                          <div className={`text-center px-2 py-1 rounded min-w-16 ${change > 0 ? 'bg-emerald-50' : change < 0 ? 'bg-rose-50' : 'bg-slate-50'}`}>
                            <p className={`text-sm font-semibold ${change > 0 ? 'text-emerald-600' : change < 0 ? 'text-rose-600' : 'text-slate-500'}`}>{change > 0 ? '+' : ''}{change.toFixed(1)}</p>
                            <p className={`text-xs ${change > 0 ? 'text-emerald-500' : change < 0 ? 'text-rose-500' : 'text-slate-400'}`}>{changePct > 0 ? '+' : ''}{changePct.toFixed(1)}%</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {/* Export + AI */}
                <div className="flex flex-col gap-3 pt-4 border-t border-purple-100">
                  <div className="flex items-center gap-2">
                    <Button variant={aiInsights ? "outline" : "default"} size="sm"
                      className={`gap-1.5 ${aiInsights ? 'text-purple-600 border-purple-200 hover:bg-purple-50' : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white'}`}
                      onClick={() => generateAiInsights(dataType, periodAMetrics, periodBMetrics)} disabled={isGenerating}>
                      {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                      {isGenerating ? 'Generating...' : (aiInsights ? 'Regenerate AI Insights' : 'Generate AI Insights')}
                    </Button>
                    {aiInsights && <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Ready</span>}
                  </div>
                  {aiInsights?.insights && (
                    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg p-4 border border-purple-200 max-h-64 overflow-y-auto">
                      <div className="flex items-center gap-2 mb-3"><Sparkles className="h-4 w-4 text-purple-600" /><span className="text-sm font-semibold text-purple-800">AI-Powered Insights</span></div>
                      <div className="prose prose-sm prose-slate max-w-none text-xs">
                        {aiInsights.insights.split('\n').slice(0, 20).map((line, i) => {
                          if (line.startsWith('## ')) return <h4 key={`insight-${i}`} className="text-purple-700 font-semibold mt-2 mb-1">{line.replace('## ', '')}</h4>;
                          if (line.startsWith('- ') || line.startsWith('* ')) return <p key={`insight-${i}`} className="ml-2 text-slate-600">{'\u2022'} {line.slice(2)}</p>;
                          if (line.trim()) return <p key={`insight-${i}`} className="text-slate-600">{line}</p>;
                          return null;
                        })}
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600">Export:</span>
                    <Button variant="outline" size="sm" className="gap-1.5 text-purple-600 border-purple-200 hover:bg-purple-50" onClick={() => exportPeriodComparison(dataType, periodAMetrics, periodBMetrics, 'pdf')} disabled={isExporting}><FileText className="h-4 w-4" />PDF</Button>
                    <Button variant="outline" size="sm" className="gap-1.5 text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={() => exportPeriodComparison(dataType, periodAMetrics, periodBMetrics, 'excel')} disabled={isExporting}><FileSpreadsheet className="h-4 w-4" />Excel</Button>
                    {aiInsights?.insights && (
                      <Button variant="default" size="sm" className="gap-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700" onClick={() => exportWithAiInsights(dataType, periodAMetrics, periodBMetrics, 'pdf', aiInsights)} disabled={isExporting}>
                        {isExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} PDF + AI
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
            {(!periodAMetrics || !periodBMetrics) && (
              <div className="text-center py-6 text-slate-500">
                <ArrowLeftRight className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">Not enough data to compare periods</p>
                <p className="text-xs text-slate-400">Import more data with different measurement dates</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderTrendCards = (measures) => {
    if (!measures || measures.length === 0) return null;
    const items = measures.filter(m => m.trend);
    if (items.length === 0) return null;
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
        {items.map((measure) => (
          <div key={measure.measureId || measure.kpiId} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <h4 className="font-medium text-slate-800 text-sm leading-tight">{measure.name}</h4>
                {measure.pillar && <p className="text-xs text-slate-500 mt-0.5">{measure.pillar}</p>}
              </div>
              <div className="w-3 h-3 rounded-full flex-shrink-0 mt-1" style={{ backgroundColor: measure.color }} />
            </div>
            <div className="flex items-center gap-3 mt-3">
              <div className="text-2xl font-bold text-slate-900">{measure.trend.latest_score?.toFixed(1) ?? measure.trend.latest_value?.toFixed(1)}</div>
              <div className={`flex items-center gap-1 text-sm font-medium ${measure.trend.change > 0 ? 'text-emerald-600' : measure.trend.change < 0 ? 'text-rose-600' : 'text-slate-500'}`}>
                {measure.trend.change > 0 ? <TrendingUp className="h-4 w-4" /> : measure.trend.change < 0 ? <TrendingDown className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
                <span>{measure.trend.change > 0 ? '+' : ''}{measure.trend.change_percent?.toFixed(1)}%</span>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
              <span>First: {measure.trend.first_score?.toFixed(1) ?? measure.trend.first_value?.toFixed(1)}</span>
              <span>Avg: {measure.trend.avg_score?.toFixed(1) ?? measure.trend.avg_value?.toFixed(1)}</span>
              <span>{measure.trend.data_point_count} pts</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const __pageTheme = getLightSectionTheme('Navy');
  const scoreTrendsHeroActions = (
    <>
      {isSuperAdmin && organizations.length > 0 && !viewAsOrg?.id && (
        <select value={selectedOrg} onChange={(e) => setSelectedOrg(e.target.value)}
          className="text-sm rounded-lg px-3 py-1.5 bg-white border border-slate-200 text-slate-800 border-0 min-w-[160px]" data-testid="score-trends-org-filter">
          <option value="all">All Organizations</option>
          {organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      )}
      {viewAsOrg?.id && (
        <span
          className="text-sm rounded-lg px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 inline-flex items-center gap-1.5"
          data-testid="score-trends-org-filter-locked"
          title="Org filter is locked while previewing — exit preview to widen the view."
        >
          <span className="h-1.5 w-1.5 rounded-full bg-amber-300 animate-pulse" />
          Scoped to {viewAsOrg.name || 'Preview Org'}
        </span>
      )}
    </>
  );
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Navigation Signals"
        description="How learning signals are converting into navigation toward business value — grouped into Missing Middle journey stages."
        theme={__pageTheme}
        icon={Compass}
        actions={scoreTrendsHeroActions}
        testId="score-trends-hero"
      />
    )}>
    <div className="px-4 md:px-8 lg:px-12 py-6" data-testid="score-trends-page">
      <div className="space-y-6">

        {/* View toggle: Navigation (default) vs All Metrics */}
        <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1" data-testid="nav-signals-view-toggle">
          <button
            type="button"
            onClick={() => setViewMode('navigation')}
            data-testid="nav-signals-view-navigation"
            className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-sm font-semibold transition-colors ${
              viewMode === 'navigation' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Compass className="h-4 w-4" /> Navigation View
          </button>
          <button
            type="button"
            onClick={() => setViewMode('metrics')}
            data-testid="nav-signals-view-metrics"
            className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-sm font-semibold transition-colors ${
              viewMode === 'metrics' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <LayoutGrid className="h-4 w-4" /> All Metrics
          </button>
        </div>

        {/* ── Navigation View ─────────────────────────────────────── */}
        {viewMode === 'navigation' && (
          hasTrendData && stageHealth?.navigation ? (
            <div className="space-y-6" data-testid="navigation-signals-view">
              <JourneyHealthRibbon stageHealth={stageHealth} />
              <NavigationSummaryCard orgId={selectedOrg} stageHealth={stageHealth} />
              <SignalPropagationChain stageHealth={stageHealth} />
              <SignalToOutcomeTab trendsData={trendsData} kpiTrendsData={kpiTrendsData} />
            </div>
          ) : (
            <Card data-testid="navigation-signals-empty">
              <CardContent>
                <div className="text-center py-12">
                  <Compass className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-2">No navigation signals yet</p>
                  <p className="text-sm text-slate-400">Import LEI baselines via Track Progress to build your journey-stage signals.</p>
                </div>
              </CardContent>
            </Card>
          )
        )}

        {/* ── All Metrics View (legacy tabs) ──────────────────────── */}
        {viewMode === 'metrics' && (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 max-w-lg">
            <TabsTrigger value="lei" data-testid="score-trends-lei-tab">
              LEI Trends
              {hasTrendData && <Badge variant="secondary" className="ml-2 text-xs">{trendsData?.measure_count}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="kpi" data-testid="score-trends-kpi-tab">
              KPI Trends
              {hasKpiTrendData && <Badge variant="secondary" className="ml-2 text-xs">{kpiTrendsData?.total_kpis_tracked}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="overlay" data-testid="score-trends-overlay-tab">
              LEI vs KPI
            </TabsTrigger>
          </TabsList>

          {/* ── LEI Tab ─────────────────────────────────────────────── */}
          <TabsContent value="lei">
            {/* Composite LEI + pillar lines + program markers + cohort filter,
                plus a separate LEI Trends by Pillar (measure components) graph —
                parity with the Learning Health Trends graph. */}
            <div className="mb-6">
              <LeiTrendsFull organizationId={selectedOrg} navigate={navigate} />
            </div>
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5 text-indigo-600" />LEI Measure Explorer &amp; Period Comparison</CardTitle></CardHeader>
              <CardContent>
                {hasTrendData ? (
                  <div className="space-y-6">
                    {/* Filters */}
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="flex-1">
                        <label className="text-sm font-medium text-slate-700 mb-1 block">Filter by Pillar</label>
                        <Select value={selectedPillar} onValueChange={(v) => { setSelectedPillar(v); setSelectedMeasure('all'); }}>
                          <SelectTrigger className="w-full"><SelectValue placeholder="All Pillars" /></SelectTrigger>
                          <SelectContent><SelectItem value="all">All Pillars</SelectItem>{getPillars().map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="flex-1">
                        <label className="text-sm font-medium text-slate-700 mb-1 block">Filter by Measure</label>
                        <Select value={selectedMeasure} onValueChange={setSelectedMeasure}>
                          <SelectTrigger className="w-full"><SelectValue placeholder="All Measures" /></SelectTrigger>
                          <SelectContent><SelectItem value="all">All Measures</SelectItem>{getMeasures().map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>

                    {renderDateRangeSlider(allLeiDates, leiDateRange, setLeiDateRange, leiStartDate, leiEndDate)}

                    {/* Chart */}
                    {chartData.length > 0 && visibleMeasures.length > 0 ? (
                      <div className="h-80 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsLineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="date" tickFormatter={formatShortDate} tick={{ fontSize: 12, fill: '#64748b' }} />
                            <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: '#64748b' }} />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend wrapperStyle={{ paddingTop: '10px' }} formatter={(value) => <span className="text-sm text-slate-600">{value}</span>} />
                            {visibleMeasures.map((measure) => (
                              <Line key={measure.measureId} type="monotone" dataKey={measure.measureId} name={measure.name} stroke={measure.color} strokeWidth={2} dot={{ r: 4, fill: measure.color }} activeDot={{ r: 6 }} connectNulls />
                            ))}
                          </RechartsLineChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="h-40 flex items-center justify-center text-slate-500">No data points for the selected filters</div>
                    )}

                    {renderTrendCards(visibleMeasures)}
                    {renderCompareSection('lei', leiPeriodA, setLeiPeriodA, leiPeriodB, setLeiPeriodB, isLeiCompareExpanded, setIsLeiCompareExpanded, leiPeriodAMetrics, leiPeriodBMetrics, leiAiInsights, isGeneratingLeiInsights, isExportingLei)}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <BarChart3 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 mb-2">No LEI trend data available yet</p>
                    <p className="text-sm text-slate-400">Import LEI baselines via Track Progress to see trends over time</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── KPI Tab ─────────────────────────────────────────────── */}
          <TabsContent value="kpi">
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5 text-rose-600" />KPI Score Trends</CardTitle></CardHeader>
              <CardContent>
                {hasKpiTrendData ? (
                  <div className="space-y-6">
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="flex-1 max-w-xs">
                        <label className="text-sm font-medium text-slate-600 mb-1 block">Filter by KPI</label>
                        <Select value={selectedKpi} onValueChange={setSelectedKpi}>
                          <SelectTrigger data-testid="kpi-trend-selector"><SelectValue placeholder="All KPIs" /></SelectTrigger>
                          <SelectContent><SelectItem value="all">All KPIs</SelectItem>{getKpiList().map(kpi => <SelectItem key={kpi.id} value={kpi.id}>{kpi.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      {/* View mode toggle */}
                      <div>
                        <label className="text-sm font-medium text-slate-600 mb-1 block">View Mode</label>
                        <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-100" data-testid="kpi-view-mode-toggle">
                          <button
                            onClick={() => setKpiViewMode('absolute')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                              kpiViewMode === 'absolute' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                            }`}
                            data-testid="kpi-mode-absolute"
                          >
                            <BarChart2 className="h-3.5 w-3.5" />
                            Absolute
                          </button>
                          <button
                            onClick={() => setKpiViewMode('normalized')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                              kpiViewMode === 'normalized' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                            }`}
                            data-testid="kpi-mode-normalized"
                          >
                            <Percent className="h-3.5 w-3.5" />
                            % Change
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Scale group legend for absolute mode */}
                    {kpiViewMode === 'absolute' && kpiScaleGroups.hasDualScale && (
                      <div className="flex items-center gap-4 text-xs text-slate-500 bg-slate-50 rounded-lg px-4 py-2 border border-slate-200" data-testid="kpi-scale-legend">
                        <span className="flex items-center gap-1.5">
                          <span className="w-4 h-0.5 bg-slate-600 inline-block" />
                          Left axis: Score / % metrics
                        </span>
                        <span className="flex items-center gap-1.5">
                          <span className="w-4 h-0.5 bg-rose-500 inline-block border-t-2 border-dashed border-rose-500" style={{borderStyle: 'dashed'}} />
                          Right axis: Monetary ($) metrics
                        </span>
                      </div>
                    )}

                    {kpiViewMode === 'normalized' && (
                      <div className="flex items-start gap-4 flex-wrap">
                        <div className="flex-1 text-xs text-slate-500 bg-blue-50 rounded-lg px-4 py-2 border border-blue-200" data-testid="kpi-normalized-info">
                          Showing <strong>% change from first data point</strong> — all KPIs on the same relative scale regardless of unit.
                          {kpiAnomalies.stats.upper != null && (
                            <span className="ml-2 text-rose-600">
                              Anomaly detection active — points beyond <strong>±{sigmaThreshold}σ</strong> ({kpiAnomalies.stats.lower?.toFixed(0)}% to {kpiAnomalies.stats.upper?.toFixed(0)}%) are highlighted in red.
                            </span>
                          )}
                        </div>
                        {/* Sigma threshold selector */}
                        <div className="flex items-center gap-2 flex-shrink-0" data-testid="sigma-threshold-selector">
                          <span className="text-xs font-medium text-slate-600 whitespace-nowrap">Sensitivity:</span>
                          <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-100">
                            {[1.5, 2, 2.5, 3].map(s => (
                              <button
                                key={s}
                                onClick={() => setSigmaThreshold(s)}
                                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                                  sigmaThreshold === s ? 'bg-white text-rose-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                                }`}
                                data-testid={`sigma-${s}`}
                              >
                                {s}σ
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {renderDateRangeSlider(allKpiDates, kpiDateRange, setKpiDateRange, kpiStartDate, kpiEndDate, 'rose')}

                    {kpiChartData.length > 0 && visibleKpis.length > 0 ? (
                      <div className="h-80 w-full" data-testid="kpi-trends-chart">
                        <ResponsiveContainer width="100%" height="100%">
                          {kpiViewMode === 'normalized' ? (
                            /* ── Normalized Mode: single Y-axis, % change + anomaly bands ── */
                            <RechartsLineChart data={kpiNormalizedData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="date" tickFormatter={formatShortDate} tick={{ fontSize: 12, fill: '#64748b' }} />
                              <YAxis
                                tick={{ fontSize: 12, fill: '#64748b' }}
                                tickFormatter={(v) => `${v >= 0 ? '+' : ''}${v.toFixed(0)}%`}
                                label={{ value: '% Change', angle: -90, position: 'insideLeft', style: { fill: '#64748b', fontSize: 11 } }}
                              />
                              {/* Nσ anomaly reference bands */}
                              {kpiAnomalies.stats.upper != null && (
                                <>
                                  <ReferenceLine y={kpiAnomalies.stats.upper} stroke="#ef4444" strokeDasharray="6 3" strokeOpacity={0.6}
                                    label={{ value: `+${sigmaThreshold}σ`, position: 'right', fill: '#ef4444', fontSize: 10 }} />
                                  <ReferenceLine y={kpiAnomalies.stats.lower} stroke="#ef4444" strokeDasharray="6 3" strokeOpacity={0.6}
                                    label={{ value: `-${sigmaThreshold}σ`, position: 'right', fill: '#ef4444', fontSize: 10 }} />
                                  <ReferenceLine y={kpiAnomalies.stats.mean} stroke="#94a3b8" strokeDasharray="3 3" strokeOpacity={0.5}
                                    label={{ value: 'mean', position: 'right', fill: '#94a3b8', fontSize: 10 }} />
                                </>
                              )}
                              <Tooltip content={<KpiTooltip />} />
                              <Legend wrapperStyle={{ paddingTop: '10px' }} formatter={(value) => <span className="text-sm text-slate-600">{value}</span>} />
                              {visibleKpis.map((kpi) => (
                                <Line key={kpi.kpiId} type="monotone" dataKey={kpi.kpiId} name={kpi.name} stroke={kpi.color} strokeWidth={2}
                                  dot={(props) => {
                                    const { cx, cy, value } = props;
                                    if (value == null || cx == null || cy == null) return null;
                                    const isOutlier = kpiAnomalies.stats.upper != null &&
                                      (value > kpiAnomalies.stats.upper || value < kpiAnomalies.stats.lower);
                                    if (isOutlier) {
                                      return (
                                        <g key={`outlier-${props.index}`}>
                                          <circle cx={cx} cy={cy} r={7} fill="#ef4444" fillOpacity={0.15} stroke="#ef4444" strokeWidth={2} />
                                          <circle cx={cx} cy={cy} r={3} fill="#ef4444" />
                                        </g>
                                      );
                                    }
                                    return <circle key={`dot-${props.index}`} cx={cx} cy={cy} r={3} fill={kpi.color} />;
                                  }}
                                  activeDot={{ r: 6 }} connectNulls />
                              ))}
                            </RechartsLineChart>
                          ) : kpiScaleGroups.hasDualScale ? (
                            /* ── Absolute Dual-Axis Mode ── */
                            <RechartsLineChart data={kpiChartData} margin={{ top: 5, right: 60, left: 20, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="date" tickFormatter={formatShortDate} tick={{ fontSize: 12, fill: '#64748b' }} />
                              <YAxis
                                yAxisId="standard"
                                orientation="left"
                                tick={{ fontSize: 12, fill: '#64748b' }}
                                label={{ value: 'Score / %', angle: -90, position: 'insideLeft', style: { fill: '#64748b', fontSize: 11 } }}
                              />
                              <YAxis
                                yAxisId="monetary"
                                orientation="right"
                                tick={{ fontSize: 12, fill: '#ef4444' }}
                                tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}K` : `$${v}`}
                                label={{ value: 'Monetary ($)', angle: 90, position: 'insideRight', style: { fill: '#ef4444', fontSize: 11 } }}
                              />
                              <Tooltip content={<KpiTooltip />} />
                              <Legend wrapperStyle={{ paddingTop: '10px' }} formatter={(value) => <span className="text-sm text-slate-600">{value}</span>} />
                              {kpiScaleGroups.standard.map((kpi) => (
                                <Line key={kpi.kpiId} yAxisId="standard" type="monotone" dataKey={kpi.kpiId} name={kpi.name}
                                  stroke={kpi.color} strokeWidth={2} dot={{ r: 4, fill: kpi.color }} activeDot={{ r: 6 }} connectNulls />
                              ))}
                              {kpiScaleGroups.monetary.map((kpi) => (
                                <Line key={kpi.kpiId} yAxisId="monetary" type="monotone" dataKey={kpi.kpiId} name={`${kpi.name} ($)`}
                                  stroke={kpi.color} strokeWidth={2} strokeDasharray="8 4"
                                  dot={{ r: 4, fill: kpi.color }} activeDot={{ r: 6 }} connectNulls />
                              ))}
                            </RechartsLineChart>
                          ) : (
                            /* ── Absolute Single-Axis Mode (all same scale) ── */
                            <RechartsLineChart data={kpiChartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="date" tickFormatter={formatShortDate} tick={{ fontSize: 12, fill: '#64748b' }} />
                              <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                              <Tooltip content={<KpiTooltip />} />
                              <Legend wrapperStyle={{ paddingTop: '10px' }} formatter={(value) => <span className="text-sm text-slate-600">{value}</span>} />
                              {visibleKpis.map((kpi) => (
                                <Line key={kpi.kpiId} type="monotone" dataKey={kpi.kpiId} name={kpi.name} stroke={kpi.color} strokeWidth={2} dot={{ r: 4, fill: kpi.color }} activeDot={{ r: 6 }} connectNulls />
                              ))}
                            </RechartsLineChart>
                          )}
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="h-40 flex items-center justify-center text-slate-500">No data points for the selected filters</div>
                    )}

                    {/* Anomaly summary */}
                    {kpiViewMode === 'normalized' && kpiAnomalies.outlierData.length > 0 && (
                      <div className="bg-rose-50 border border-rose-200 rounded-lg p-4" data-testid="anomaly-summary">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="h-5 w-5 text-rose-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-rose-800 mb-2">
                              Anomaly Detection: {kpiAnomalies.outlierData.length} data point{kpiAnomalies.outlierData.length !== 1 ? 's' : ''} exceed ±{sigmaThreshold}σ bounds
                            </p>
                            <div className="space-y-1">
                              {kpiAnomalies.outlierData.slice(0, 6).map((row) => {
                                const outlierKpis = Object.keys(row).filter(k => k.endsWith('_outlier'));
                                return outlierKpis.map(ok => {
                                  const kpiId = ok.replace('_outlier', '');
                                  const kpi = visibleKpis.find(k => k.kpiId === kpiId);
                                  const val = row[ok];
                                  return (
                                    <div key={`${row.date}-${kpiId}`} className="flex items-center gap-2 text-xs text-rose-700">
                                      <div className="w-2 h-2 rounded-full bg-rose-500" />
                                      <span className="font-mono">{formatShortDate(row.date)}</span>
                                      <span className="font-medium">{kpi?.name || kpiId}:</span>
                                      <span className="font-bold">{val >= 0 ? '+' : ''}{val?.toFixed(1)}%</span>
                                      <span className="text-rose-400">({val > kpiAnomalies.stats.upper ? 'above' : 'below'} {sigmaThreshold}σ)</span>
                                    </div>
                                  );
                                });
                              })}
                              {kpiAnomalies.outlierData.length > 6 && (
                                <p className="text-xs text-rose-500 italic mt-1">...and {kpiAnomalies.outlierData.length - 6} more</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* KPI trend summary cards */}
                    {visibleKpis.length > 0 && kpiTrendsData?.by_kpi && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {visibleKpis.map((kpi) => {
                          const kpiData = kpiTrendsData.by_kpi[kpi.kpiId];
                          if (!kpiData?.trend) return null;
                          const trend = kpiData.trend;
                          return (
                            <div key={kpi.kpiId} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                              <div className="flex items-start justify-between mb-2">
                                <h4 className="font-medium text-slate-800 text-sm">{kpi.name}</h4>
                                <div className="w-3 h-3 rounded-full flex-shrink-0 mt-1" style={{ backgroundColor: kpi.color }} />
                              </div>
                              <div className="flex items-center gap-3 mt-3">
                                <div className="text-2xl font-bold text-slate-900">{trend.latest_value?.toFixed(1)}</div>
                                <div className={`flex items-center gap-1 text-sm font-medium ${trend.change > 0 ? 'text-emerald-600' : trend.change < 0 ? 'text-rose-600' : 'text-slate-500'}`}>
                                  {trend.change > 0 ? <TrendingUp className="h-4 w-4" /> : trend.change < 0 ? <TrendingDown className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
                                  <span>{trend.change > 0 ? '+' : ''}{trend.change_percent?.toFixed(1)}%</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                                <span>First: {trend.first_value?.toFixed(1)}</span>
                                <span>Avg: {trend.avg_value?.toFixed(1)}</span>
                                <span>{trend.data_point_count} pts</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Anomaly Alert Subscription */}
                    {kpiViewMode === 'normalized' && (
                      <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="anomaly-alert-subscription">
                        <button
                          onClick={() => setIsSubExpanded(!isSubExpanded)}
                          className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <Bell className="h-5 w-5 text-rose-600" />
                            <span className="font-semibold text-slate-800 text-sm">Anomaly Alert Subscription</span>
                            {anomalySub?.email_alerts && (
                              <Badge variant="outline" className="text-emerald-600 border-emerald-300 text-[10px]">Email Active</Badge>
                            )}
                            {anomalySub?.in_app_alerts && (
                              <Badge variant="outline" className="text-blue-600 border-blue-300 text-[10px]">In-App Active</Badge>
                            )}
                          </div>
                          {isSubExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                        </button>
                        {isSubExpanded && anomalySub && (
                          <div className="p-4 pt-0 space-y-4 border-t border-slate-100">
                            <p className="text-xs text-slate-500">
                              Get notified when imported data contains anomalies that exceed your configured threshold. Alerts trigger automatically on CSV import via the Data Hub.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                              {/* In-App Alerts */}
                              <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 cursor-pointer hover:border-blue-300 transition-colors" data-testid="sub-in-app-toggle">
                                <input
                                  type="checkbox"
                                  checked={anomalySub.in_app_alerts}
                                  onChange={(e) => saveAnomalySub({ in_app_alerts: e.target.checked })}
                                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                />
                                <div>
                                  <div className="flex items-center gap-1.5">
                                    <Bell className="h-3.5 w-3.5 text-blue-600" />
                                    <span className="text-sm font-medium text-slate-800">In-App Alerts</span>
                                  </div>
                                  <p className="text-[11px] text-slate-500 mt-0.5">Notification center</p>
                                </div>
                              </label>
                              {/* Email Alerts */}
                              <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 cursor-pointer hover:border-emerald-300 transition-colors" data-testid="sub-email-toggle">
                                <input
                                  type="checkbox"
                                  checked={anomalySub.email_alerts}
                                  onChange={(e) => saveAnomalySub({ email_alerts: e.target.checked })}
                                  className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                />
                                <div>
                                  <div className="flex items-center gap-1.5">
                                    <Mail className="h-3.5 w-3.5 text-emerald-600" />
                                    <span className="text-sm font-medium text-slate-800">Email Alerts</span>
                                  </div>
                                  <p className="text-[11px] text-slate-500 mt-0.5">To {anomalySub.email || 'your email'}</p>
                                </div>
                              </label>
                              {/* Sigma Threshold */}
                              <div className="p-3 rounded-lg border border-slate-200">
                                <div className="flex items-center gap-1.5 mb-2">
                                  <AlertCircle className="h-3.5 w-3.5 text-rose-500" />
                                  <span className="text-sm font-medium text-slate-800">Alert Threshold</span>
                                </div>
                                <div className="inline-flex rounded border border-slate-200 p-0.5 bg-slate-50">
                                  {[1.5, 2, 2.5, 3].map(s => (
                                    <button
                                      key={s}
                                      onClick={() => saveAnomalySub({ sigma_threshold: s })}
                                      disabled={isSubSaving}
                                      className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                                        anomalySub.sigma_threshold === s ? 'bg-white text-rose-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                                      }`}
                                      data-testid={`sub-sigma-${s}`}
                                    >
                                      {s}σ
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                            {/* Monitored Data Types */}
                            <div>
                              <p className="text-xs font-medium text-slate-600 mb-2">Monitor data types:</p>
                              <div className="flex flex-wrap gap-1.5" data-testid="sub-data-types">
                                {[
                                  { id: 'lei_averaged', label: 'LEI Averaged', color: 'teal' },
                                  { id: 'lei_raw', label: 'LEI Raw', color: 'teal' },
                                  { id: 'pe_capability', label: 'PE Capability', color: 'indigo' },
                                  { id: 'pe_retention', label: 'PE Retention', color: 'indigo' },
                                  { id: 'roe_motivation', label: 'ROE Motivation', color: 'amber' },
                                  { id: 'roe_l2', label: 'ROE L2', color: 'amber' },
                                  { id: 'roe_l3', label: 'ROE L3', color: 'amber' },
                                  { id: 'kpi_actuals', label: 'KPI Actuals', color: 'rose' },
                                ].map(dt => {
                                  const isOn = (anomalySub.data_types || []).includes(dt.id);
                                  return (
                                    <button
                                      key={dt.id}
                                      onClick={() => {
                                        const current = anomalySub.data_types || [];
                                        const updated = isOn ? current.filter(d => d !== dt.id) : [...current, dt.id];
                                        saveAnomalySub({ data_types: updated });
                                      }}
                                      disabled={isSubSaving}
                                      className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                                        isOn
                                          ? `bg-${dt.color}-100 text-${dt.color}-800 border-${dt.color}-300`
                                          : 'bg-slate-50 text-slate-400 border-slate-200 hover:border-slate-300'
                                      }`}
                                      data-testid={`sub-dtype-${dt.id}`}
                                    >
                                      {dt.label}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {renderCompareSection('kpi', kpiPeriodA, setKpiPeriodA, kpiPeriodB, setKpiPeriodB, isKpiCompareExpanded, setIsKpiCompareExpanded, kpiPeriodAMetrics, kpiPeriodBMetrics, kpiAiInsights, isGeneratingKpiInsights, isExportingKpi)}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <BarChart3 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 mb-2">No KPI trend data available yet</p>
                    <p className="text-sm text-slate-400">Import KPI actuals via Track Progress to see trends over time</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          {/* ── LEI vs KPI Overlay Tab ────────────────────────────────── */}
          <TabsContent value="overlay">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <ArrowLeftRight className="h-5 w-5 text-purple-600" />
                      LEI vs KPI Overlay
                    </CardTitle>
                    <p className="text-sm text-slate-500 mt-1">Select LEI measures and KPIs to overlay on the same chart and explore causal relationships</p>
                  </div>
                  {/* View mode toggle */}
                  <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-100" data-testid="overlay-view-mode-toggle">
                    <button
                      onClick={() => setOverlayViewMode('absolute')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        overlayViewMode === 'absolute' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                      }`}
                      data-testid="overlay-mode-absolute"
                    >
                      <BarChart2 className="h-3.5 w-3.5" />
                      Absolute
                    </button>
                    <button
                      onClick={() => setOverlayViewMode('normalized')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        overlayViewMode === 'normalized' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                      }`}
                      data-testid="overlay-mode-normalized"
                    >
                      <Percent className="h-3.5 w-3.5" />
                      % Change
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {hasTrendData || hasKpiTrendData ? (
                  <div className="space-y-6">
                    {/* Selectors */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* LEI selector */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-semibold text-teal-700 flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-teal-500" />
                            LEI Measures
                            <Badge variant="outline" className="text-teal-600 border-teal-300 text-xs">{selectedLeiOverlays.length}/6</Badge>
                          </h3>
                          {selectedLeiOverlays.length > 0 && (
                            <Button variant="ghost" size="sm" className="text-xs h-7 text-slate-500"
                              onClick={() => setSelectedLeiOverlays([])} data-testid="overlay-clear-lei">Clear</Button>
                          )}
                        </div>
                        <div className="max-h-48 overflow-y-auto space-y-1 border rounded-lg p-2 bg-slate-50" data-testid="overlay-lei-list">
                          {availableLeiMeasures.length > 0 ? availableLeiMeasures.map((m, idx) => {
                            const isSelected = selectedLeiOverlays.includes(m.id);
                            const colorIdx = selectedLeiOverlays.indexOf(m.id);
                            return (
                              <button key={m.id} onClick={() => toggleOverlay(m.id, selectedLeiOverlays, setSelectedLeiOverlays)}
                                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 ${
                                  isSelected ? 'bg-teal-100 text-teal-800 font-medium' : 'hover:bg-slate-100 text-slate-700'
                                }`} data-testid={`overlay-lei-${m.id}`}>
                                {isSelected && <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: LEI_OVERLAY_COLORS[colorIdx % LEI_OVERLAY_COLORS.length] }} />}
                                <span className="truncate">{m.name}</span>
                                <span className="text-[10px] text-slate-400 ml-auto flex-shrink-0">{m.pillar?.replace('P', '').split(' ')[0]}</span>
                              </button>
                            );
                          }) : <p className="text-xs text-slate-400 py-3 text-center">No LEI data available</p>}
                        </div>
                      </div>

                      {/* KPI selector */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-semibold text-rose-700 flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-rose-500" />
                            KPI Measures
                            <Badge variant="outline" className="text-rose-600 border-rose-300 text-xs">{selectedKpiOverlays.length}/6</Badge>
                          </h3>
                          {selectedKpiOverlays.length > 0 && (
                            <Button variant="ghost" size="sm" className="text-xs h-7 text-slate-500"
                              onClick={() => setSelectedKpiOverlays([])} data-testid="overlay-clear-kpi">Clear</Button>
                          )}
                        </div>
                        <div className="max-h-48 overflow-y-auto space-y-1 border rounded-lg p-2 bg-slate-50" data-testid="overlay-kpi-list">
                          {availableKpis.length > 0 ? availableKpis.map((k) => {
                            const isSelected = selectedKpiOverlays.includes(k.id);
                            const colorIdx = selectedKpiOverlays.indexOf(k.id);
                            return (
                              <button key={k.id} onClick={() => toggleOverlay(k.id, selectedKpiOverlays, setSelectedKpiOverlays)}
                                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 ${
                                  isSelected ? 'bg-rose-100 text-rose-800 font-medium' : 'hover:bg-slate-100 text-slate-700'
                                }`} data-testid={`overlay-kpi-${k.id}`}>
                                {isSelected && <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: KPI_OVERLAY_COLORS[colorIdx % KPI_OVERLAY_COLORS.length] }} />}
                                <span className="truncate">{k.name}</span>
                              </button>
                            );
                          }) : <p className="text-xs text-slate-400 py-3 text-center">No KPI data available</p>}
                        </div>
                      </div>
                    </div>

                    {/* Active selections summary */}
                    {(selectedLeiOverlays.length > 0 || selectedKpiOverlays.length > 0) && (
                      <div className="flex flex-wrap gap-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
                        {selectedLeiOverlays.map((mid, i) => {
                          const m = availableLeiMeasures.find(x => x.id === mid);
                          return (
                            <Badge key={mid} className="text-xs py-1 px-2.5 gap-1.5 cursor-pointer hover:opacity-80 transition-opacity" style={{ backgroundColor: LEI_OVERLAY_COLORS[i % LEI_OVERLAY_COLORS.length] + '20', color: LEI_OVERLAY_COLORS[i % LEI_OVERLAY_COLORS.length], borderColor: LEI_OVERLAY_COLORS[i % LEI_OVERLAY_COLORS.length] + '40' }} variant="outline"
                              onClick={() => setSelectedLeiOverlays(prev => prev.filter(id => id !== mid))}
                              data-testid={`remove-lei-pill-${i}`}>
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: LEI_OVERLAY_COLORS[i % LEI_OVERLAY_COLORS.length] }} />
                              LEI: {m?.name || mid}
                              <span className="ml-1 font-bold">&times;</span>
                            </Badge>
                          );
                        })}
                        {selectedKpiOverlays.map((kid, i) => {
                          const k = availableKpis.find(x => x.id === kid);
                          return (
                            <Badge key={kid} className="text-xs py-1 px-2.5 gap-1.5 cursor-pointer hover:opacity-80 transition-opacity" style={{ backgroundColor: KPI_OVERLAY_COLORS[i % KPI_OVERLAY_COLORS.length] + '20', color: KPI_OVERLAY_COLORS[i % KPI_OVERLAY_COLORS.length], borderColor: KPI_OVERLAY_COLORS[i % KPI_OVERLAY_COLORS.length] + '40' }} variant="outline"
                              onClick={() => setSelectedKpiOverlays(prev => prev.filter(id => id !== kid))}
                              data-testid={`remove-kpi-pill-${i}`}>
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: KPI_OVERLAY_COLORS[i % KPI_OVERLAY_COLORS.length] }} />
                              KPI: {k?.name || kid}
                              <span className="ml-1 font-bold">&times;</span>
                            </Badge>
                          );
                        })}
                      </div>
                    )}

                    {/* Overlay Chart */}
                    {overlayChartData.length > 0 ? (
                      <div className="space-y-3">
                        {overlayViewMode === 'normalized' && (
                          <div className="text-xs text-slate-500 bg-blue-50 rounded-lg px-4 py-2 border border-blue-200" data-testid="overlay-normalized-info">
                            Showing <strong>% change from first data point</strong> — both LEI and KPI trends on the same relative scale for direct comparison.
                          </div>
                        )}
                        <div className="h-96 w-full">
                          <ResponsiveContainer width="100%" height="100%">
                            {overlayViewMode === 'normalized' ? (
                              /* ── Normalized Overlay: single Y-axis ── */
                              <RechartsLineChart data={overlayNormalizedData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis dataKey="date" tickFormatter={formatShortDate} tick={{ fontSize: 11, fill: '#64748b' }} />
                                <YAxis
                                  tick={{ fontSize: 11, fill: '#64748b' }}
                                  tickFormatter={(v) => `${v >= 0 ? '+' : ''}${v.toFixed(0)}%`}
                                  label={{ value: '% Change from Baseline', angle: -90, position: 'insideLeft', style: { fill: '#64748b', fontSize: 11 } }}
                                />
                                <Tooltip content={<OverlayTooltip />} />
                                <Legend wrapperStyle={{ paddingTop: '12px' }} formatter={(value) => <span className="text-xs text-slate-600">{value}</span>} />
                                {selectedLeiOverlays.map((mid, i) => {
                                  const m = availableLeiMeasures.find(x => x.id === mid);
                                  return (
                                    <Line key={`lei_${mid}`} type="monotone" dataKey={`lei_${mid}`}
                                      name={`LEI: ${m?.name || mid}`} stroke={LEI_OVERLAY_COLORS[i % LEI_OVERLAY_COLORS.length]}
                                      strokeWidth={2.5} dot={{ r: 4, fill: LEI_OVERLAY_COLORS[i % LEI_OVERLAY_COLORS.length] }}
                                      activeDot={{ r: 6 }} connectNulls />
                                  );
                                })}
                                {selectedKpiOverlays.map((kid, i) => {
                                  const k = availableKpis.find(x => x.id === kid);
                                  return (
                                    <Line key={`kpi_${kid}`} type="monotone" dataKey={`kpi_${kid}`}
                                      name={`KPI: ${k?.name || kid}`} stroke={KPI_OVERLAY_COLORS[i % KPI_OVERLAY_COLORS.length]}
                                      strokeWidth={2.5} strokeDasharray="8 4"
                                      dot={{ r: 4, fill: KPI_OVERLAY_COLORS[i % KPI_OVERLAY_COLORS.length], strokeWidth: 0 }}
                                      activeDot={{ r: 6 }} connectNulls />
                                  );
                                })}
                              </RechartsLineChart>
                            ) : (
                              /* ── Absolute Overlay: dual Y-axis ── */
                              <RechartsLineChart data={overlayChartData} margin={{ top: 5, right: 60, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis dataKey="date" tickFormatter={formatShortDate} tick={{ fontSize: 11, fill: '#64748b' }} />
                                <YAxis yAxisId="lei" orientation="left" domain={[0, 100]} tick={{ fontSize: 11, fill: '#0d9488' }}
                                  label={{ value: 'LEI Score', angle: -90, position: 'insideLeft', style: { fill: '#0d9488', fontSize: 12 } }} />
                                <YAxis yAxisId="kpi" orientation="right" tick={{ fontSize: 11, fill: '#ef4444' }}
                                  label={{ value: 'KPI Value', angle: 90, position: 'insideRight', style: { fill: '#ef4444', fontSize: 12 } }} />
                                <Tooltip content={<OverlayTooltip />} />
                                <Legend wrapperStyle={{ paddingTop: '12px' }} formatter={(value) => <span className="text-xs text-slate-600">{value}</span>} />
                                {selectedLeiOverlays.map((mid, i) => {
                                  const m = availableLeiMeasures.find(x => x.id === mid);
                                  return (
                                    <Line key={`lei_${mid}`} yAxisId="lei" type="monotone" dataKey={`lei_${mid}`}
                                      name={`LEI: ${m?.name || mid}`} stroke={LEI_OVERLAY_COLORS[i % LEI_OVERLAY_COLORS.length]}
                                      strokeWidth={2.5} dot={{ r: 4, fill: LEI_OVERLAY_COLORS[i % LEI_OVERLAY_COLORS.length] }}
                                      activeDot={{ r: 6 }} connectNulls />
                                  );
                                })}
                                {selectedKpiOverlays.map((kid, i) => {
                                  const k = availableKpis.find(x => x.id === kid);
                                  return (
                                    <Line key={`kpi_${kid}`} yAxisId="kpi" type="monotone" dataKey={`kpi_${kid}`}
                                      name={`KPI: ${k?.name || kid}`} stroke={KPI_OVERLAY_COLORS[i % KPI_OVERLAY_COLORS.length]}
                                      strokeWidth={2.5} strokeDasharray="8 4"
                                      dot={{ r: 4, fill: KPI_OVERLAY_COLORS[i % KPI_OVERLAY_COLORS.length], strokeWidth: 0 }}
                                      activeDot={{ r: 6 }} connectNulls />
                                  );
                                })}
                              </RechartsLineChart>
                            )}
                          </ResponsiveContainer>
                        </div>
                      </div>
                    ) : (
                      <div className="h-52 flex flex-col items-center justify-center text-slate-400 bg-slate-50 rounded-xl border border-dashed border-slate-300">
                        <ArrowLeftRight className="h-10 w-10 mb-3 text-slate-300" />
                        <p className="font-medium text-slate-500">Select LEI measures and KPIs above</p>
                        <p className="text-sm mt-1">Pick at least one from each side to see the overlay chart</p>
                      </div>
                    )}

                    {/* Insight callout */}
                    {overlayChartData.length > 0 && (
                      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <Sparkles className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-purple-800">
                            <p className="font-medium mb-1">Reading the Overlay</p>
                            <ul className="space-y-1 text-purple-700">
                              {overlayViewMode === 'normalized' ? (
                                <>
                                  <li>All metrics converted to <span className="font-semibold">% change from their first data point</span> — directly comparable regardless of original scale</li>
                                  <li><span className="font-semibold">Solid lines</span> = LEI measures, <span className="font-semibold">Dashed lines</span> = KPI measures</li>
                                  <li>Metrics moving in the <span className="font-semibold">same direction</span> suggest correlation; LEI leading KPI by 1-3 months suggests causation</li>
                                </>
                              ) : (
                                <>
                                  <li><span className="font-semibold">Solid lines</span> = LEI measures (left Y-axis, scored 0-100)</li>
                                  <li><span className="font-semibold">Dashed lines</span> = KPI measures (right Y-axis, native scale)</li>
                                  <li>When LEI trends <span className="font-semibold">lead</span> KPI movements by 1-3 months, it suggests a <span className="font-semibold">causal relationship</span></li>
                                  <li>Switch to <span className="font-semibold">% Change</span> mode to compare trends on the same scale</li>
                                </>
                              )}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <ArrowLeftRight className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 mb-2">No data available for overlay</p>
                    <p className="text-sm text-slate-400">Import both LEI baselines and KPI actuals to compare trends</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>
        )}
      </div>
    </div>
    </PageShell>
  );
}
